import React, { useEffect, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ReactComponent as AvatarSvg } from '../../../media/images/avatar.svg';
import {
    faShoppingBag,
    faSignOut,
    faBars,
    faTimes,
    faChevronDown,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Routes } from 'routes';
import { ExternalLinks, FlipGiveAccountMenuUrls } from 'externalLinks';
import { getLogoutUrl } from 'features/auth/components/withAuth';
import { Notifications } from './Notifications';
import styles from './StickyHeaderMenuStyles';
import { useRootSelector } from 'common/features/featuresReducer';
import { colors } from 'styles/settings';
import { ReactComponent as LogoIconLightBg } from '../../../media/images/LogoIconLightBg.svg';
import { ReactComponent as FlipGiveIcon } from '../../../media/images/flipgive/FlipGiveIcon.svg';
import { IS_FLIPGIVE } from 'common/env';

interface StickyHeaderProps {
    onMenuToggle?: () => void;
    isNavCollapsed?: boolean;
}

export const StickyHeader = ({ onMenuToggle, isNavCollapsed }: StickyHeaderProps) => {
    const divHeaderRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const LEFT_NAV_WIDTH = 260;
        const HEADER_MIN_WIDTH = 450;
        const updateHeaderWidth = () => {
            if (divHeaderRef.current) {
                const headerWidth = document.body.getBoundingClientRect().width - LEFT_NAV_WIDTH;
                divHeaderRef.current.style['width'] = `${
                    headerWidth <= HEADER_MIN_WIDTH ? HEADER_MIN_WIDTH : headerWidth
                }px`;
            }
        };

        updateHeaderWidth();
        window.addEventListener('resize', updateHeaderWidth);

        return () => {
            window.removeEventListener('resize', updateHeaderWidth);
        };
    }, []);

    return (
        <div className={styles.container}>
            <div className="flex items-center gap-2 lg:hidden flex-1">
                <Link to={Routes.Shop} className="flex items-center">
                    {IS_FLIPGIVE ? (
                        <FlipGiveIcon height={36} width={36} />
                    ) : (
                        <LogoIconLightBg height={36} width={36} />
                    )}
                </Link>

                {onMenuToggle && (
                    <button
                        onClick={onMenuToggle}
                        className="coord_dash_nav_toggle p-3 flex items-center justify-center"
                        aria-label="Toggle navigation menu"
                    >
                        <FontAwesomeIcon
                            icon={isNavCollapsed ? faBars : faTimes}
                            className="text-2xl"
                            style={{ color: colors.brand }}
                        />
                    </button>
                )}
            </div>

            <div className={styles.logo}>
                <Link
                    to={Routes.Brands}
                    className="flex items-center focus:outline-none no-underline text-brand-dark py-2.5 px-5 rounded-full focus:bg-pale-blue/40 hover:bg-pale-blue/40"
                >
                    <FontAwesomeIcon
                        icon={faShoppingBag}
                        style={{ width: '20px', height: '20px' }}
                    />
                    <span className="text-sm ml-2 py-0.5">Personal Shopping</span>
                </Link>
            </div>

            <div>
                <div className={styles.menuIcon} style={{ width: 1, height: 44 }}></div>
            </div>
            {!IS_FLIPGIVE && <Notifications />}
            <Menu />
        </div>
    );
};

const Menu = () => {
    const [logoutUrl, setLogoutUrl] = useState('');
    const storageId = useRootSelector((s) => s.store.account.userId?.substr(0, 8));

    useEffect(() => {
        (async () => {
            const _logoutUrl = await getLogoutUrl();
            setLogoutUrl(_logoutUrl || '');
        })();
    }, []);

    // NOTE: The welcome message rendering is controlled via local storage
    // we want to mark it as _seen_ so it doesn't render next time the user logs in.
    const disableWelcomeMessage = () => {
        window.localStorage.setItem(`${storageId}_has_seen_welcome_message`, 'true');
        if (window.localStorage.getItem(`${storageId}_tempidx`) === '0') {
            window.localStorage.setItem(`${storageId}_tempidx`, '1');
        }
        window.location.href = logoutUrl;
    };

    return (
        <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger
                className={`${styles.avatarContainer} coord_dash_account_trigger`}
            >
                <div
                    style={{ width: '24px', height: '24px' }}
                    className={`${styles.avatar} coord_dash_account_avatar`}
                >
                    <AvatarSvg />
                </div>
                <div className={styles.menuBtn}>Account</div>
                <FontAwesomeIcon icon={faChevronDown} className="hidden md:block" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal className={styles.menuDropdown}>
                <DropdownMenu.Content className={styles.menuDropdown}>
                    <DropdownMenu.Item className="focus:outline-none focus-visible:outline-none">
                        <Link to={Routes.CoordinatorDashboardHome} className={styles.menuItemsBold}>
                            Coordinator Dashboard
                        </Link>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item>
                        <Link
                            to={Routes.ShopAccountSettingsDashboardTab}
                            className={styles.menuItemsBold}
                        >
                            Personal Dashboard
                        </Link>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item>
                        <Link to={Routes.Brands} className={styles.menuItemsBold}>
                            Personal Shopping
                        </Link>
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className={styles.menuSeparator} />

                    <DropdownMenu.Item>
                        <a
                            href={
                                IS_FLIPGIVE
                                    ? FlipGiveAccountMenuUrls.coordinatorResources
                                    : Routes.SupportOrganization
                            }
                            target="_blank"
                            className={styles.menuItems}
                        >
                            Coordinator Resources
                        </a>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item>
                        <a
                            href={
                                IS_FLIPGIVE
                                    ? FlipGiveAccountMenuUrls.participantResources
                                    : Routes.SupportParticipant
                            }
                            target="_blank"
                            className={styles.menuItems}
                        >
                            Participant Resources
                        </a>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item>
                        <a
                            href={ExternalLinks.HelpCenterAccountDropdown}
                            className={styles.menuItems}
                        >
                            {IS_FLIPGIVE ? 'Help Centre' : 'Help Center'}
                        </a>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item>
                        <a
                            href={IS_FLIPGIVE ? FlipGiveAccountMenuUrls.contactUs : Routes.Contact}
                            target="_blank"
                            className={styles.menuItems}
                        >
                            Contact Us
                        </a>
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className={styles.signOutSeparator} />

                    <span onClick={disableWelcomeMessage} className={styles.menuLogout}>
                        <FontAwesomeIcon icon={faSignOut} /> Sign Out
                    </span>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};
